// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../../base/GirafeHTMLElement';
import { Overlay } from 'ol';
import MapPosition from '../../../tools/state/mapposition';
import areEqual from '../../../tools/state/brain/equality';

class MapCustomContextMenuComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = [
    './style.css',
    '../../../styles/common.css',
    '../mapcontextmenu.css',
    '../../../../node_modules/ol-ext/overlay/Popup.css'
  ];

  private get map() {
    return this.context.mapManager.getMap();
  }

  // We use a static property/getter for the overlay, because OpenLayer is recreating a new object each time when we do
  // an addOverlay(). But as multiple instances of the Map (when using multiple <geogirafe-map ... /> in API context)
  // should NOT share the same overlay, we need to create a new one for each map instance (indentified by Map's custom
  // UUID Property).
  private static readonly contextMenuOverlayByMap: { [mapId: string]: Overlay } = {};
  private get contextMenuOverlay() {
    const uuid = this.map.get('uuid') as string;
    return MapCustomContextMenuComponent.contextMenuOverlayByMap[uuid];
  }
  private set contextMenuOverlay(value: Overlay) {
    const uuid = this.map.get('uuid') as string;
    MapCustomContextMenuComponent.contextMenuOverlayByMap[uuid] = value;
  }

  get tooltipTitle() {
    const title = this.state.position.tooltip?.title;
    if (!title) {
      return '';
    }
    if (title.includes('<')) {
      return this.htmlUnsafe(title);
    }
    return this.htmlUnsafe(this.context.i18nManager.getTranslation(title));
  }

  get tooltipContent() {
    const content = this.state.position.tooltip?.content;
    if (!content) {
      return '';
    }
    if (content.includes('<')) {
      return this.htmlUnsafe(content);
    }
    return this.htmlUnsafe(this.context.i18nManager.getTranslation(content));
  }

  public constructor() {
    super('custom-map-context-menu');
  }

  override render() {
    if (this.state.position.tooltip) {
      super.render();
      this.contextMenuOverlay.setPosition(this.state.position.tooltip.position);
    } else {
      super.renderEmpty();
    }
  }

  public closeMenu() {
    this.state.position.tooltip = undefined;
    this.render();
  }

  private registerEvents() {
    this.subscribe('position', (oldPos?: MapPosition, newPos?: MapPosition) => {
      if (!areEqual(oldPos?.tooltip, newPos?.tooltip)) {
        this.render();
      }
    });
    this.subscribe('position.tooltip', () => {
      this.render();
    });
  }

  protected override connectedCallback() {
    super.connectedCallback();
    if (!this.contextMenuOverlay) {
      this.contextMenuOverlay = new Overlay({
        element: this,
        autoPan: { animation: { duration: 250 } }
      });
      this.map.addOverlay(this.contextMenuOverlay);
    }

    this.registerEvents();
  }
}

export default MapCustomContextMenuComponent;
